import { useEffect, useMemo, useState } from "react";

import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { useDebounce } from "@uidotdev/usehooks";
import { useTranslation } from "react-i18next";

import { AccountCreateEditModal } from "../../components/accounts/AccountCreateEditModal";
import AccountTable, { IAccountRow } from "../../components/accounts/AccountsTable";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useAccounts from "../../data/useAccounts";
import useContacts from "../../data/useContacts";
import { AccountType } from "../../models/account/IAccount";

export default function AccountList() {
  const { t } = useTranslation("accounts");
  const { setInfo } = usePageNameContext();
  const [includeEbay, setIncludeEbay] = useState<boolean>(false);
  const [hideAuto, setHideAuto] = useState<boolean>(false);
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const { accounts } = useAccounts("AccountList", showArchived);
  // Unfiltered contact set - the includeEbay/hideAuto filters are applied to
  // accounts below, this is only used to find each account's contact.
  const { contacts } = useContacts(true, false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredRows, setFilteredRows] = useState<IAccountRow[]>([]);
  const [addOpen, setAddOpen] = useState(false);

  // Every account gets exactly one row, even if it has no linked contact -
  // deriving rows from contacts instead hides accounts without a contact and
  // creates gaps in the account number sequence shown to users. Filtering
  // mirrors the old /contact endpoint (ContactController.GetContacts):
  //  - includeEbay=false drops eBay-type accounts and eBay-member contacts
  //  - hideAuto=true ("Show Only Regulars Accounts") drops Auto-type accounts
  const allRows = useMemo(() => {
    const contactList = contacts.data?.data ?? [];

    return (accounts.data?.data ?? [])
      .filter((account) => !hideAuto || account.accountType !== AccountType.Auto)
      .map((account) => {
        const contactsForAccount = contactList.filter(
          (contact) => contact.accountId === account.id
        );

        // Don't surface eBay-member contacts as the row's contact when eBay
        // is excluded, matching the old endpoint's email rule.
        const eligibleContacts = includeEbay
          ? contactsForAccount
          : contactsForAccount.filter(
              (contact) => !contact.email?.includes("@members.ebay.com")
            );

        const primaryContact = eligibleContacts.find(
          (contact) => contact.id === account.primaryContactId
        );
        const firstContact = [...eligibleContacts].sort((a, b) =>
          a.created > b.created ? 1 : -1
        )[0];

        return {
          row: {
            account,
            contact: primaryContact ?? firstContact ?? null,
          } as IAccountRow,
          // An account whose contacts are ALL eBay-member contacts is an eBay
          // account in all but type - drop it rather than render it as N/A.
          // Contact-less accounts (length 0) are kept and show N/A.
          isEbayOnly: contactsForAccount.length > 0 && eligibleContacts.length === 0,
        };
      })
      .filter(({ row, isEbayOnly }) => {
        if (includeEbay) return true;
        return row.account.accountType !== AccountType.eBay && !isEbayOnly;
      })
      .map(({ row }) => row);
  }, [accounts.data?.data, contacts.data?.data, includeEbay, hideAuto]);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const search = (row: IAccountRow, searchTerm: string) => {
    let st = searchTerm.toLowerCase();

    return (
      row.contact?.fullname?.toLowerCase().includes(st) ||
      row.account.name?.toLowerCase().includes(st) ||
      row.contact?.email?.toLowerCase().includes(st) ||
      String(row.account.accountNumber).includes(st)
    );
  };

  useEffect(() => {
    let tempRows: IAccountRow[] = [];

    if (!debouncedSearchTerm || debouncedSearchTerm === "") {
      tempRows = allRows;
    } else {
      tempRows = allRows.filter((row) => search(row, debouncedSearchTerm));
    }

    setFilteredRows(tempRows);
  }, [debouncedSearchTerm, allRows]);

  useEffect(() => {
    setInfo({
      name: t("Accounts"),
      desc: t("View and manage all accounts here"),
    });
  }, [t]);

  return accounts.isLoading || contacts.isLoading ? (
    <div className="mt-8 min-h-[50vh] my-4 p-4 bg-white rounded-lg shadow">
      <LoadingWheel />
    </div>
  ) : !accounts.data || !accounts.data.data || accounts.isError || !contacts.data || !contacts.data.data || contacts.isError ? (
    <>
      <p className="text-lg text-red-600 font-medium">
        {t("An error occurred retrieving accounts")}
      </p>
    </>
  ) : (
    <>
      <AccountCreateEditModal open={addOpen} setOpen={setAddOpen} />
      <div className="">
        <div className="mt-10 relative flex items-stretch justify-between flex-grow focus-within:z-10">
          <div className="flex items-center space-x-4">

            <div>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-96 pl-10 sm:text-sm"
                placeholder={t("Contact/Account Name, Email or Account No")}
              />
            </div>

            <div className="flex gap-x-1 items-center">
              <label className="text-sm font-medium text-gray-500">
                {t("Include eBay Accounts")}
              </label>
              <input
                onChange={(e) => setIncludeEbay(e.target.checked)}
                checked={includeEbay}
                type="checkbox"
              />
            </div>

            <div className="flex gap-x-1 items-center">
              <label className="text-sm font-medium text-gray-500">
                {t("Show Only Regulars Accounts")}
              </label>
              <input
                onChange={(e) => setHideAuto(e.target.checked)}
                checked={hideAuto}
                type="checkbox"
              />
            </div>

            <div className="flex gap-x-1 items-center">
              <label className="text-sm font-medium text-gray-500">
                {t("Show Archived Accounts")}
              </label>
              <input
                onChange={(e) => setShowArchived(e.target.checked)}
                checked={showArchived}
                type="checkbox"
              />
            </div>
          </div>

          <ButtonInput
            label={t("Add Account")}
            classes=""
            isSubmit={false}
            onClick={() => setAddOpen(true)}
          />

        </div>

        <AccountTable data={filteredRows} />
      </div>
    </>
  );
}